/**
 * FloatingPatientChip
 * CCH Care — Phase 3
 *
 * Appears after the ADL page header scrolls out of view.
 * Shows resident name, shift, and live signed count.
 * Fades in/out smoothly using IntersectionObserver.
 *
 * ESLint fixes:
 * - Removed unused useRef import
 * - Fixed duplicate 'transform' key in style object
 */

import { useState, useEffect } from 'react';
import { COLORS } from '../../config/constants';

export default function FloatingPatientChip({ sentinelRef, residentName, shiftType, signedCount, totalSections = 8 }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sentinelRef?.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0, rootMargin: '-56px 0px 0px 0px' } // 56px = app nav height
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [sentinelRef]);

  const allSigned = signedCount === totalSections;

  return (
    <div style={{
      position: 'fixed',
      top: 56, // below app nav
      left: '50%',
      zIndex: 50,
      pointerEvents: 'none',
      transition: 'opacity 0.2s ease, transform 0.2s ease',
      opacity: visible ? 1 : 0,
      transform: visible
        ? 'translateX(-50%) translateY(0)'
        : 'translateX(-50%) translateY(-8px)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: COLORS.primary,
        color: '#fff',
        borderRadius: 999,
        padding: '6px 14px 6px 10px',
        fontSize: 12, fontWeight: 600,
        boxShadow: '0 2px 12px rgba(27,107,147,0.35)',
        whiteSpace: 'nowrap',
      }}>
        <span style={{ fontSize: 14 }}>👤</span>
        <span>{residentName}</span>
        <span style={{ opacity: 0.7 }}>·</span>
        <span style={{ opacity: 0.85 }}>{shiftType} Shift</span>
        <span style={{ opacity: 0.7 }}>·</span>
        <span style={{
          fontWeight: 800,
          color: allSigned ? '#7DFFB3' : '#FFD97D',
        }}>
          {signedCount}/{totalSections} signed
        </span>
      </div>
    </div>
  );
}
